import { useEffect, useRef, useState } from 'react'
import { Alert, Box, Button, IconButton, TextField, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { Fund, Valuation } from '../../api/types'
import { useOpsApi } from '../../api/context'
import { apiError } from '../../api/errors'
import { navDeviation, navTotalOf } from '../../calc/preview'
import { parseAmount } from '../../validation'
import { InfoHint } from '../../components/InfoHint'
import { SectionCard } from '../../components/SectionCard'
import { formatCzk, formatPercent, todayIso } from './format'

interface NavRow {
  /** Stabilní klíč řádku — index se při mazání posouvá, id ne. */
  id: number
  label: string
  amountText: string
}

/**
 * Standardní složky NAV fondu (zadání 4. 9. 2026). Předvyplňují se vždy, aby je obsluha
 * nemusela vypisovat a aby na žádnou nezapomněla. Řádek, do kterého se nevyplní částka,
 * se do ocenění nezapočítá — u fondu, který danou složku nedrží, se prostě nechá prázdný.
 */
const STANDARDNI_POLOZKY_NAV = [
  'J&T',
  'IB',
  'Cash',
  'APEX Blockchain',
  'Apex One',
  'Zálohy na MGM fee',
]

/**
 * Řádky ocenění: nejdřív standardní sada, pak názvy z posledního ocenění, které v ní
 * nejsou (vlastní položky obsluhy zůstanou zachované). Částky vždy prázdné — NAV se
 * zadává ručně ke každému datu.
 */
function buildInitialRows(latest: Valuation | undefined, nextId: () => number): NavRow[] {
  const rows = STANDARDNI_POLOZKY_NAV.map((label) => ({ id: nextId(), label, amountText: '' }))
  const znameNazvy = new Set(STANDARDNI_POLOZKY_NAV.map((l) => l.toLowerCase()))
  if (latest) {
    latest.items
      .filter((item) => item.label.trim() && !znameNazvy.has(item.label.trim().toLowerCase()))
      .forEach((item) => rows.push({ id: nextId(), label: item.label, amountText: '' }))
  }
  return rows
}

interface NavValuationCardProps {
  fund: Fund
  /** Nejnovější ocenění fondu (valuations[0]) — předloha názvů položek a báze odchylky. */
  latestValuation: Valuation | undefined
  onCreated: () => void
}

/**
 * Nové ocenění fondu (fáze 4, jen CEO): volné řádky NAV položek, živý součet,
 * varování odchylky proti minulému NAV. NAV se zadává vždy BEZ nových vkladů.
 */
export function NavValuationCard({ fund, latestValuation, onCreated }: NavValuationCardProps) {
  const api = useOpsApi()
  const rowSeq = useRef(0)
  const nextRowId = () => (rowSeq.current += 1)

  const [rows, setRows] = useState<NavRow[]>(() => buildInitialRows(latestValuation, nextRowId))
  const [date, setDate] = useState<string>(todayIso)
  // Chyby částky ukazujeme až po pokusu o uložení (attempted) nebo po opuštění pole (touched).
  const [attempted, setAttempted] = useState(false)
  const [touched, setTouched] = useState<Set<number>>(new Set())
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Přepnutí fondu i uložení ocenění mění referenci latestValuation → reinicializace
  // řádků z názvů posledního ocenění téhož fondu (částky se vyprázdní).
  useEffect(() => {
    setRows(buildInitialRows(latestValuation, nextRowId))
    setDate(todayIso())
    setAttempted(false)
    setTouched(new Set())
    setError(null)
  }, [latestValuation])

  // Živý součet jen z řádků s vyplněnou částkou — přes navTotalOf (žádný float součet v UI).
  const validItems = rows
    .map((r) => parseAmount(r.amountText))
    .filter((amount): amount is number => amount !== null)
    .map((amount) => ({ amount }))
  const navCelkem = navTotalOf(validItems)

  // Varování až když je co srovnávat — prázdný formulář (NAV 0) by jinak
  // trvale hlásil odchylku 100 %.
  const deviation = navDeviation(navCelkem, fund.lastValuation?.navTotal)
  const showDeviation = validItems.length > 0 && deviation !== null && deviation >= 0.1

  const updateRow = (id: number, patch: Partial<NavRow>) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)))
  const addRow = () =>
    setRows((prev) => [...prev, { id: nextRowId(), label: '', amountText: '' }])
  const removeRow = (id: number) =>
    setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== id) : prev))

  // Řádek se do ocenění počítá, teprve když má vyplněnou částku. Názvy se předvyplňují
  // (viz STANDARDNI_POLOZKY_NAV), takže samotný název ještě neznamená, že fond tu složku
  // drží — jinak by prázdné předvyplněné řádky blokovaly uložení hláškou o chybějící částce.
  const isIncluded = (row: NavRow) => row.amountText.trim() !== ''

  // Které standardní složky zůstaly nevyplněné. Nezakazujeme to (fond nemusí držet všechno),
  // ale je to vidět: zapomenutá položka snižuje NAV, a tím i cenu jednotky, takže by klienti
  // za stejné peníze dostali víc jednotek.
  const nevyplnene = rows
    .filter((r) => !r.amountText.trim() && STANDARDNI_POLOZKY_NAV.includes(r.label.trim()))
    .map((r) => r.label.trim())

  // NAV položka smí být i záporná (závazek, debet u brokera) — na rozdíl od částky
  // vkladu proto nepoužíváme validateAmount (> 0), jen kontrolu, že jde o číslo.
  const navItemError = (text: string): string | null =>
    parseAmount(text) === null ? 'Zadejte částku číslem' : null

  const amountErrorFor = (row: NavRow): string | undefined => {
    if (!isIncluded(row)) return undefined
    const err = navItemError(row.amountText)
    if (!err) return undefined
    if (attempted) return err
    if (touched.has(row.id) && row.amountText.trim()) return err
    return undefined
  }
  const labelErrorFor = (row: NavRow): string | undefined =>
    attempted && row.amountText.trim() !== '' && !row.label.trim() ? 'Zadejte název' : undefined

  const handleSubmit = async () => {
    setAttempted(true)
    const included = rows.filter(isIncluded)
    if (included.length === 0) {
      setError('Zadejte alespoň jednu položku ocenění')
      return
    }
    // Neúplné řádky (chybí název/částka) hlásíme inline — API request nesestavujeme.
    const invalid = included.some((r) => !r.label.trim() || navItemError(r.amountText) !== null)
    if (invalid) {
      setError(null)
      return
    }

    const items = included.map((r) => ({
      label: r.label.trim(),
      amount: parseAmount(r.amountText) as number,
    }))

    setSubmitting(true)
    setError(null)
    try {
      await api.createValuation({ fundKey: fund.key, date, items })
      // Reset částek (názvy necháme); reload v rodiči navíc přepošle nové ocenění.
      setRows((prev) => prev.map((r) => ({ ...r, amountText: '' })))
      setAttempted(false)
      setTouched(new Set())
      onCreated()
    } catch (err) {
      setError(apiError(err, 'Uložení ocenění selhalo'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <SectionCard
      title="Nové ocenění (NAV)"
      info={
        <InfoHint
          title="Jak funguje ocenění"
          lines={[
            'NAV = součet zadaných položek (hodnota portfolia fondu k datu).',
            'Cena jednotky = NAV / celkový počet jednotek fondu k datu ocenění.',
            'Cena je jedna pro všechny třídy (A i B) — třída ovlivňuje jen sazby poplatků.',
            'Cena instrumentu (graf a hodnota portfolií klientů) se tím NEMĚNÍ — aktualizuje se zvlášť v kartě Cena instrumentu.',
          ]}
          sx={{ color: '#fff' }}
        />
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {rows.map((row) => (
            <Box key={row.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <TextField
                size="small"
                label="Položka"
                value={row.label}
                onChange={(e) => updateRow(row.id, { label: e.target.value })}
                error={Boolean(labelErrorFor(row))}
                helperText={labelErrorFor(row)}
                disabled={submitting}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                label="Částka"
                value={row.amountText}
                onChange={(e) => updateRow(row.id, { amountText: e.target.value })}
                onBlur={() => setTouched((prev) => new Set(prev).add(row.id))}
                error={Boolean(amountErrorFor(row))}
                helperText={amountErrorFor(row)}
                placeholder="1 000 000"
                disabled={submitting}
                sx={{ width: 180 }}
              />
              <IconButton
                size="small"
                color="error"
                onClick={() => removeRow(row.id)}
                disabled={submitting || rows.length === 1}
                sx={{ mt: 0.5 }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>

        <Box>
          <Button size="small" startIcon={<AddIcon />} onClick={addRow} disabled={submitting}>
            Přidat položku
          </Button>
        </Box>

        <TextField
          size="small"
          type="date"
          label="Datum ocenění"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          disabled={submitting}
          sx={{ width: 200 }}
        />

        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          NAV celkem: {formatCzk(navCelkem)}
        </Typography>

        {nevyplnene.length > 0 && (
          <Alert severity="info">
            Bez částky, takže se do NAV nezapočítá: {nevyplnene.join(', ')}. Pokud fond tuhle
            složku drží, doplň ji — chybějící položka snižuje NAV i cenu jednotky.
          </Alert>
        )}

        {showDeviation && deviation !== null && fund.lastValuation && (
          <Alert severity="warning">
            NAV se od minulého ocenění ({formatCzk(fund.lastValuation.navTotal)}) liší o{' '}
            {formatPercent(deviation)} — zkontroluj položky.
          </Alert>
        )}

        <Alert severity="info" variant="outlined">
          NAV zadávej vždy BEZ nových vkladů — ty se připisují až přepočtem.
        </Alert>

        {error && <Alert severity="error">{error}</Alert>}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            Potvrdit ocenění
          </Button>
        </Box>
      </Box>
    </SectionCard>
  )
}
